import type { SurfaceMap } from '@ai-secure-twin/shared';

/**
 * Where each box and line of the surface diagram goes.
 *
 * Layered left to right: the outside world, then the hosts a scanner reaches, then the components
 * those hosts serve, then whatever those components declare they call. Observed and declared are
 * drawn differently and never merged — a solid line is something a scan saw, a dashed one is
 * something a person said.
 *
 * Pure and deterministic, so the same map always draws the same picture.
 */

export const NODE_WIDTH = 228;
export const NODE_HEIGHT = 84;
const COLUMN_GAP = 76;
const ROW_GAP = 18;
const PADDING = 12;
const MAX_COLUMN = 6;

export type LayoutNodeKind = 'internet' | 'host' | 'component' | 'unmapped';

export interface LayoutNode {
  /** `internet`, `host:{name}`, `component:{id}` or `unmapped`. */
  id: string;
  kind: LayoutNodeKind;
  column: number;
  x: number;
  y: number;
  /** For hosts: whether any scan observed it, or it is only declared on a component. */
  observed: boolean;
}

/**
 * `observed` — a scan placed endpoints of this component on this host.
 * `declared` — a person said so: a component's host with nothing observed there yet, or a
 * component's `connectsTo`.
 */
export type EdgeStyle = 'observed' | 'declared';

export interface LayoutEdge {
  from: string;
  to: string;
  style: EdgeStyle;
}

export interface SurfaceLayout {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  width: number;
  height: number;
}

export const hostNodeId = (host: string) => `host:${host}`;
export const componentNodeId = (id: string) => `component:${id}`;

export function layoutSurface(map: SurfaceMap): SurfaceLayout {
  const components = map.components;
  const observedHosts = new Set(map.hosts.map((h) => h.host));
  const declaredHosts = components.flatMap((c) => c.component.hosts);
  const hosts = [...new Set([...observedHosts, ...declaredHosts])].sort();

  const edges: LayoutEdge[] = hosts.map((h) => ({
    from: 'internet',
    to: hostNodeId(h),
    style: observedHosts.has(h) ? 'observed' : 'declared',
  }));

  for (const h of map.hosts) {
    for (const id of h.componentIds) {
      edges.push({ from: hostNodeId(h.host), to: componentNodeId(id), style: 'observed' });
    }
  }
  for (const c of components) {
    for (const host of c.component.hosts) {
      const seen = map.hosts.find((h) => h.host === host)?.componentIds.includes(c.component.id);
      if (!seen) {
        edges.push({
          from: hostNodeId(host),
          to: componentNodeId(c.component.id),
          style: 'declared',
        });
      }
    }
  }

  const unmappedHosts = [...new Set(map.unmapped.endpoints.map((e) => e.host))].sort();
  const hasUnmapped = map.unmapped.endpoints.length > 0 || map.unmapped.findings.length > 0;
  for (const host of unmappedHosts) {
    edges.push({ from: hostNodeId(host), to: 'unmapped', style: 'observed' });
  }

  const ids = new Set(components.map((c) => c.component.id));
  for (const c of components) {
    for (const target of c.component.connectsTo) {
      if (ids.has(target)) {
        edges.push({
          from: componentNodeId(c.component.id),
          to: componentNodeId(target),
          style: 'declared',
        });
      }
    }
  }

  /*
   * Columns. A component reached from a host sits beside the hosts. One reached only through
   * another component's `connectsTo` sits one column past its furthest caller — computed by
   * relaxing the edges a bounded number of times, so a declared cycle cannot loop for ever.
   */
  const column = new Map<string, number>();
  for (const c of components) {
    const fromHost = edges.some(
      (e) => e.to === componentNodeId(c.component.id) && e.from.startsWith('host:'),
    );
    if (fromHost || c.component.pathPrefixes.length > 0 || c.endpoints.length > 0) {
      column.set(c.component.id, 2);
    }
  }
  for (let pass = 0; pass < components.length; pass += 1) {
    let changed = false;
    for (const c of components) {
      if (column.get(c.component.id) === 2) continue;
      const callers = components
        .filter((other) => other.component.connectsTo.includes(c.component.id))
        .map((other) => column.get(other.component.id))
        .filter((col): col is number => col !== undefined);
      if (callers.length === 0) continue;
      const next = Math.min(MAX_COLUMN, Math.max(...callers) + 1);
      if (column.get(c.component.id) !== next) {
        column.set(c.component.id, next);
        changed = true;
      }
    }
    if (!changed) break;
  }

  const placed: { id: string; kind: LayoutNodeKind; column: number; observed: boolean }[] = [
    { id: 'internet', kind: 'internet', column: 0, observed: true },
    ...hosts.map((h) => ({
      id: hostNodeId(h),
      kind: 'host' as const,
      column: 1,
      observed: observedHosts.has(h),
    })),
    ...components.map((c) => ({
      id: componentNodeId(c.component.id),
      kind: 'component' as const,
      // Reached by nothing at all: drawn past everything, on its own.
      column: column.get(c.component.id) ?? 3,
      observed: c.endpoints.length > 0,
    })),
    ...(hasUnmapped
      ? [{ id: 'unmapped', kind: 'unmapped' as const, column: 2, observed: true }]
      : []),
  ];

  // Empty columns are closed up, so a map with no internal components has no gap on the right.
  const usedColumns = [...new Set(placed.map((p) => p.column))].sort((a, b) => a - b);
  const columnIndex = new Map(usedColumns.map((c, i) => [c, i]));

  const perColumn = new Map<number, typeof placed>();
  for (const p of placed) perColumn.set(p.column, [...(perColumn.get(p.column) ?? []), p]);
  const tallest = Math.max(...[...perColumn.values()].map((list) => list.length));
  const height = PADDING * 2 + tallest * NODE_HEIGHT + (tallest - 1) * ROW_GAP;

  const nodes: LayoutNode[] = [];
  for (const [col, list] of perColumn) {
    const columnHeight = list.length * NODE_HEIGHT + (list.length - 1) * ROW_GAP;
    const top = (height - columnHeight) / 2;
    list.forEach((p, row) => {
      nodes.push({
        id: p.id,
        kind: p.kind,
        column: col,
        observed: p.observed,
        x: PADDING + (columnIndex.get(col) ?? 0) * (NODE_WIDTH + COLUMN_GAP),
        y: top + row * (NODE_HEIGHT + ROW_GAP),
      });
    });
  }

  return {
    nodes,
    edges,
    width: PADDING * 2 + usedColumns.length * NODE_WIDTH + (usedColumns.length - 1) * COLUMN_GAP,
    height,
  };
}

/** A curve from the right edge of one box to the left edge of another. */
export function edgePath(from: LayoutNode, to: LayoutNode): string {
  const x1 = from.x + NODE_WIDTH;
  const y1 = from.y + NODE_HEIGHT / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_HEIGHT / 2;
  if (x2 > x1) {
    const dx = Math.max(30, (x2 - x1) / 2);
    return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
  }
  // Same column or backwards (a declared cycle): loop out to the right and back in.
  const loop = 40;
  return `M ${x1} ${y1} C ${x1 + loop} ${y1}, ${to.x + NODE_WIDTH + loop} ${y2}, ${to.x + NODE_WIDTH} ${y2}`;
}
